import React, {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import usePerformanceTasksApi from './config';
import { useAuth } from '../../context/AuthContext';
import useDeepLinkParams from '../../hooks/useDeepLinkParams';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

// Task 32 — İK görev paneli (itiraz incelemesi + gelişmeli takip). Liste
// EvaluationPeriodsPage'in server-side DataTable + satır aksiyonu desenini
// izler. "Tamamla" yalnız PM.Write yetkisiyle görünür (Write, Read'i zaten
// kapsar) ve yalnız açık (status !== 'done') satırlarda sunulur.
//
// Gelen Kutum derin bağlantısı (2026-10-28): ?taskId= ile gelinince o görevin
// "Görevi Tamamla" onayı doğrudan açılır (sayfanın kayda özgü TEK eylemi;
// görev tablonun bu sayfasında olmasa da — tamamlama ucu kimlikle çalışır,
// zaten kapanmış görevi sunucu 409 ile reddeder). Diyalog kapanınca
// parametre adresten silinir.
const Container = styled.div``;

const emptyConfirm = { isOpen: false, row: null };

export default function PerformanceTasksPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [queryParams, setQueryParams] = useState({ pageSize: 20, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(emptyConfirm);
  const [completing, setCompleting] = useState(false);

  const {
    fetching, data, pagination, columns: baseColumns, complete, findById,
  } = usePerformanceTasksApi(queryParams);
  const { values: { taskId: linkedTaskId }, clear: clearDeepLink } = useDeepLinkParams(['taskId']);
  const canComplete = canWrite('PM');

  const handleDataStateChange = useCallback((_action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const openConfirm = useCallback((row) => setConfirm({ isOpen: true, row }), []);
  const closeConfirm = useCallback(() => {
    setConfirm(emptyConfirm);
    clearDeepLink();
  }, [clearDeepLink]);

  // Derin bağlantıda görev ÖNCE yüklenir: onay diyaloğu görevi adıyla gösterir; görev yoksa ya da
  // zaten tamamlanmışsa (kart eskidi) diyalog açılmaz, parametre temizlenir.
  useEffect(() => {
    if (!linkedTaskId || !canComplete) return undefined;
    let cancelled = false;
    findById(linkedTaskId).then((task) => {
      if (cancelled) return;
      if (task && task.status !== 'done') setConfirm({ isOpen: true, row: task });
      else clearDeepLink();
    }).catch(() => { if (!cancelled) clearDeepLink(); });
    return () => { cancelled = true; };
  }, [linkedTaskId, canComplete, findById, clearDeepLink]);

  const handleConfirm = useCallback(async () => {
    if (!confirm.row) return;
    setCompleting(true);
    await complete(confirm.row.id);
    setCompleting(false);
    closeConfirm();
  }, [confirm.row, complete, closeConfirm]);

  const columns = useMemo(() => {
    if (!canComplete) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'rowActions',
        label: ' ',
        options: {
          filter: false,
          sort: false,
          download: false,
          print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row || row.status === 'done') return null;
            const actions = [{
              type: 'complete',
              label: t('performance.tasks.complete'),
              onClick: () => openConfirm(row),
            }];
            return <OptionsMenu actions={actions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, canComplete, data, openConfirm, t]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={confirm.isOpen}
        title={t('performance.tasks.completeConfirmTitle')}
        message={confirm.row?.title
          ? t('performance.tasks.completeConfirmMessageNamed', { title: confirm.row.title })
          : t('performance.tasks.completeConfirmMessage')}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
        loading={completing}
        type="info"
      />

      <DataTable
        title={t('performance.tasks.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
        canCreateNewRecord={false}
      />
    </Container>
  );
}
